// Language Context for providing language data throughout the app
import React, { createContext, useContext, useState, useEffect } from 'react';
import { loadLanguage, getCurrentLanguageCode } from '../utils/languageLoader';
import { gameConfig } from '../config/gameConfig';

// Create the context
const LanguageContext = createContext();

/**
 * Custom hook to use language data in components
 * @returns {Object} Object containing language data, current language code, and setter function
 */
export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};

/**
 * Helper function to load a specific language
 * @param {string} langCode - Language code to load
 * @returns {Promise<Object>} The language object
 */
const loadLanguageByCode = async (langCode) => {
    const cleanCode = langCode.toLowerCase().trim();
    
    try {
        const languageModule = await import(`../config/lang.${cleanCode}.js`);
        return languageModule.language;
    } catch (error) {
        // If language file doesn't exist, try default
        const defaultCode = gameConfig.defaultLanguage;
        if (cleanCode !== defaultCode) {
            try {
                const defaultModule = await import(`../config/lang.${defaultCode}.js`);
                return defaultModule.language;
            } catch (defaultError) {
                throw new Error('Failed to load language files');
            }
        }
        throw error;
    }
};

/**
 * Language Provider component
 * Loads and provides language data to all child components
 */
export const LanguageProvider = ({ children }) => {
    const [language, setLanguage] = useState(null);
    const [languageCode, setLanguageCode] = useState(getCurrentLanguageCode());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Function to change language dynamically
    const changeLanguage = async (newLangCode) => {
        try {
            if (gameConfig.debugMode) {
                console.log('🌐 Changing language to:', newLangCode);
            }
            
            setLoading(true);
            const newLanguageData = await loadLanguageByCode(newLangCode);
            setLanguage(newLanguageData);
            setLanguageCode(newLangCode);
            
            if (gameConfig.debugMode) {
                console.log('✅ Language changed successfully to:', newLangCode);
            }
        } catch (err) {
            console.error('Failed to change language:', err);
            // Don't set error state, just keep current language
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const loadLanguageData = async () => {
            try {
                setLoading(true);
                setError(null);
                
                const languageData = await loadLanguage();
                setLanguage(languageData);
                setLanguageCode(getCurrentLanguageCode());
                
                if (gameConfig.debugMode) {
                    console.log('🌐 Language context initialized:', getCurrentLanguageCode());
                }
            } catch (err) {
                console.error('Failed to load language:', err);
                setError(err);
            } finally {
                setLoading(false);
            }
        };

        loadLanguageData();
    }, []);

    // Show loading state
    if (loading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                backgroundColor: '#000',
                color: '#fff',
                fontFamily: 'Arial, sans-serif'
            }}>
                <div>Loading...</div>
            </div>
        );
    }

    // Show error state
    if (error) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                backgroundColor: '#000',
                color: '#ff6b6b',
                fontFamily: 'Arial, sans-serif',
                textAlign: 'center',
                padding: '20px'
            }}>
                <div>
                    <h2>Language Loading Error</h2>
                    <p>{error.message}</p>
                </div>
            </div>
        );
    }

    return (
        <LanguageContext.Provider value={{ language, languageCode, changeLanguage, loading }}>
            {children}
        </LanguageContext.Provider>
    );
};

export default LanguageContext;

